/*
 * useDraftInvalidation
 *
 * Drops the current vessel's draft when a planner input that fed into it
 * changes — except when the change comes from switching the planner vessel
 * (its settings load), so drafts survive vessel switches. In Manual mode a
 * manual draft only loses its analysis: it falls back to a sketch so the
 * user's waypoints stay.
 */

import { useCallback, useEffect, useRef } from "react";
import { useRouteStore } from "@/features/route/state/RouteStore.jsx";
import {
  selectDraftRouteIdForVessel,
  selectRenderableRoute,
} from "@/features/route/state/RouteSelectors.jsx";
import { createDraftDiscardState } from "@/features/route/state/RouteState.jsx";
import { normalizeSketchRoute } from "@/features/route/state/RouteAdapters.jsx";
import {
  isManualDraft,
  manualRoutePoints,
} from "@/features/voyagePlanner/manualSketch";

export default function useDraftInvalidation({
  currentVesselId,
  plannerVesselSettings,
  speedProfileData,
  consumptionProfileData,
  applyRnwSafety,
  cwViaAreasGeojson,
  keepManualWaypoints = false,
  onAfterClear,
}) {
  const { state, replaceRoute } = useRouteStore();

  const stateRef = useRef(state);
  stateRef.current = state;
  const keepManualRef = useRef(keepManualWaypoints);
  keepManualRef.current = keepManualWaypoints;

  const clearDraftForVessel = useCallback(
    (vesselId) => {
      const draftRouteId = selectDraftRouteIdForVessel(
        stateRef.current,
        vesselId,
      );
      if (draftRouteId == null) return false;
      const draft = selectRenderableRoute(stateRef.current, draftRouteId);
      if (draft?.sketch) return false;
      replaceRoute(
        draftRouteId,
        keepManualRef.current && isManualDraft(draft)
          ? normalizeSketchRoute(draftRouteId, manualRoutePoints(draft))
          : createDraftDiscardState(draftRouteId),
      );
      return true;
    },
    [replaceRoute],
  );

  const mountGuardRef = useRef(true);
  const inputsVesselRef = useRef(currentVesselId);
  const onAfterClearRef = useRef(onAfterClear);
  onAfterClearRef.current = onAfterClear;
  const currentVesselIdRef = useRef(currentVesselId);
  currentVesselIdRef.current = currentVesselId;

  // Keyed on planner inputs only — a vessel change alone must not clear.
  useEffect(() => {
    const vesselId = currentVesselIdRef.current;
    if (mountGuardRef.current) {
      mountGuardRef.current = false;
      inputsVesselRef.current = vesselId;
      return;
    }
    const vesselChanged = inputsVesselRef.current !== vesselId;
    inputsVesselRef.current = vesselId;
    // A vessel switch loads that vessel's settings — not an edit.
    if (vesselChanged) return;
    if (clearDraftForVessel(vesselId)) onAfterClearRef.current?.();
  }, [
    plannerVesselSettings,
    speedProfileData,
    consumptionProfileData,
    applyRnwSafety,
    cwViaAreasGeojson,
    clearDraftForVessel,
  ]);
}
